import getTimepointStats from './getTimepointStats';

/**
 * RECIP 1.0 AI assessment.
 *
 *   TTV         = sum of selected PSMA-VOL over LESION + NEW links
 *                 (timepoint.Lesions[].volume, set by getTimepoints)
 *   PSMA-VOL    = CR: TTV 0
 *                 PD: change >= +minVolumeIncrease vs the increase comparator
 *                 PR: change <= -minVolumeDecrease vs the decrease comparator
 *                 SD: otherwise
 *   New lesion  = Present if a NEW link has an ROI at this timepoint
 *                 (fromBL: NEW links are absent at BL by definition;
 *                  fromPrevious: also absent at the previous timepoint)
 *   Overall     = CR: CR+Absent | PR: PR+Absent | PD: PD+Present | NE | otherwise SD
 *
 * Configurable via scenario.protocol (defaults = RECIP 1.0 as published):
 *   longitudinalIncreaseRule / longitudinalDecreaseRule:
 *     'fromPrevious' | 'fromBL' | 'fromNadir'    (default 'fromPrevious')
 *   longitudinalPriority: 'None' | 'Increase' | 'Decrease' (default 'None')
 *     must not be 'None' when the two rules differ (both PD and PR can hold)
 *   newLesionRule: 'fromBL' | 'fromPrevious'   (default 'fromBL')
 *
 * Any lesion whose PSMA-VOL is not evaluable (psmaNe) at the current or a
 * comparator timepoint makes PSMA-VOL NE: a missing value is never summed as 0.
 *
 * Output keeps the shape of the other guidelines: { ...timepoint, xxxResponse: { result, message } }.
 * Messages hold exactly one ':' because TimepointInfo renders message.split(':')[1].
 */

export const RECIP_RESPONSE = {
  PSMA_VOL: {
    CR: 'PSMA-VOL-CR',
    PR: 'PSMA-VOL-PR',
    SD: 'PSMA-VOL-SD',
    PD: 'PSMA-VOL-PD',
    NE: 'NE',
  },
  NEW_LESION: { PRESENT: 'Present', ABSENT: 'Absent', NE: 'NE' },
  OVERALL: {
    CR: 'RECIP-CR',
    PR: 'RECIP-PR',
    SD: 'RECIP-SD',
    PD: 'RECIP-PD',
    NE: 'NE',
  },
};

export const LONGITUDINAL_RULES = ['fromPrevious', 'fromBL', 'fromNadir'];
export const LONGITUDINAL_PRIORITIES = ['None', 'Increase', 'Decrease'];
export const NEW_LESION_RULES = ['fromBL', 'fromPrevious'];

const { PSMA_VOL, NEW_LESION, OVERALL } = RECIP_RESPONSE;
const EMPTY = { result: '', message: '' };

/** Resolve rule options with defaults; invalid combinations → error string. */
export function resolveRecipRules(criteria = {}) {
  const increaseRule = criteria.longitudinalIncreaseRule || 'fromPrevious';
  const decreaseRule = criteria.longitudinalDecreaseRule || 'fromPrevious';
  const priority = criteria.longitudinalPriority || 'None';
  const newLesionRule = criteria.newLesionRule || 'fromBL';
  let error = null;
  if (!(criteria.minVolumeIncrease > 0) || !(criteria.minVolumeDecrease > 0)) {
    error = 'volume change criteria missing';
  } else if (
    !LONGITUDINAL_RULES.includes(increaseRule) ||
    !LONGITUDINAL_RULES.includes(decreaseRule) ||
    !LONGITUDINAL_PRIORITIES.includes(priority) ||
    !NEW_LESION_RULES.includes(newLesionRule)
  ) {
    error = 'unknown longitudinal rule';
  } else if (increaseRule !== decreaseRule && priority === 'None') {
    error = 'longitudinalPriority required when rules differ';
  }
  return { increaseRule, decreaseRule, priority, newLesionRule, error };
}

/** Index of the comparator timepoint for `index` (index >= 1). */
function getComparatorIndex(stats, index, rule) {
  switch (rule) {
    case 'fromBL':
      return 0;
    case 'fromNadir': {
      // nadir on TTV (getTimepointStats' own nadir uses sumDiameter)
      let nadir = 0;
      for (let i = 1; i < index; i++) {
        if (stats[i].summation.sumVolume < stats[nadir].summation.sumVolume) {
          nadir = i;
        }
      }
      return nadir;
    }
    case 'fromPrevious':
    default:
      return index - 1;
  }
}

function hasPsmaNe(timepoint, lesion_ids) {
  return timepoint.Lesions.some(
    (l) => lesion_ids.includes(l.link_id) && l.psmaNe
  );
}

function getChange(current, comparator) {
  const ttv = current.summation.sumVolume;
  const base = comparator.summation.sumVolume;
  const ratio = base === 0 ? Infinity : (ttv - base) / base;
  const text = Number.isFinite(ratio)
    ? `${ratio >= 0 ? '+' : ''}${(ratio * 100).toFixed(1)}% from ${comparator.id}`
    : `${comparator.id} TTV 0 mm³`;
  return { ratio, text };
}

function getPsmaVolResponse(stats, index, lesion_ids, criteria, rules) {
  if (rules.error) {
    return { result: PSMA_VOL.NE, message: `PSMA-VOL: guideline ${rules.error}` };
  }
  const current = stats[index];
  const incComparator = stats[getComparatorIndex(stats, index, rules.increaseRule)];
  const decComparator = stats[getComparatorIndex(stats, index, rules.decreaseRule)];
  if (
    [current, incComparator, decComparator].some((t) => hasPsmaNe(t, lesion_ids))
  ) {
    return {
      result: PSMA_VOL.NE,
      message: 'PSMA-VOL: not evaluable (reference or PSMA-VOL missing)',
    };
  }
  const ttv = current.summation.sumVolume;
  if (ttv === 0) {
    return { result: PSMA_VOL.CR, message: 'PSMA-VOL: TTV 0 mm³' };
  }
  const inc = getChange(current, incComparator);
  const dec = getChange(current, decComparator);
  const isIncrease = inc.ratio >= criteria.minVolumeIncrease;
  const isDecrease = dec.ratio <= -criteria.minVolumeDecrease;
  const text =
    rules.increaseRule === rules.decreaseRule
      ? `TTV ${ttv} mm³ (${inc.text})`
      : `TTV ${ttv} mm³ (${inc.text} / ${dec.text})`;

  let result = PSMA_VOL.SD;
  if (isIncrease && isDecrease) {
    result = rules.priority === 'Increase' ? PSMA_VOL.PD : PSMA_VOL.PR;
  } else if (isIncrease) {
    result = PSMA_VOL.PD;
  } else if (isDecrease) {
    result = PSMA_VOL.PR;
  }
  return { result, message: `PSMA-VOL: ${text}` };
}

function getNewLesionResponse(stats, index, new_ids, rules) {
  const current = stats[index];
  const previous = stats[index - 1];
  const existsAt = (t, id) =>
    t.Lesions.some((l) => l.link_id === id && l.exist);
  const present = new_ids.filter(
    (id) =>
      existsAt(current, id) &&
      (rules.newLesionRule !== 'fromPrevious' || !existsAt(previous, id))
  );
  return present.length > 0
    ? { result: NEW_LESION.PRESENT, message: `New Lesion: ${present.length} present` }
    : { result: NEW_LESION.ABSENT, message: 'New Lesion: none' };
}

function getOverallResponse(psmaVol, newLesion) {
  if (psmaVol === PSMA_VOL.NE || newLesion === NEW_LESION.NE) return OVERALL.NE;
  const present = newLesion === NEW_LESION.PRESENT;
  if (psmaVol === PSMA_VOL.PD && present) return OVERALL.PD;
  if (psmaVol === PSMA_VOL.PR && !present) return OVERALL.PR;
  if (psmaVol === PSMA_VOL.CR && !present) return OVERALL.CR;
  return OVERALL.SD;
}

export default function getRecipAssessments(timepoints, links, criteria) {
  const rules = resolveRecipRules(criteria);
  const lesion_ids = links
    .filter((l) => ['LESION', 'NEW'].includes(l.type))
    .map((l) => l.link_id);
  const new_ids = links.filter((l) => l.type === 'NEW').map((l) => l.link_id);
  const stats = getTimepointStats(timepoints, lesion_ids, [], criteria);

  return stats.map((current, index) => {
    if (index === 0) {
      return {
        ...current,
        psmaVolResponse: EMPTY,
        newLesionResponse: EMPTY,
        overallResponse: EMPTY,
      };
    }
    const psmaVolResponse = getPsmaVolResponse(stats, index, lesion_ids, criteria, rules);
    const newLesionResponse = getNewLesionResponse(stats, index, new_ids, rules);
    const result = getOverallResponse(psmaVolResponse.result, newLesionResponse.result);
    return {
      ...current,
      psmaVolResponse,
      newLesionResponse,
      overallResponse: { result, message: `Overall: ${result}` },
    };
  });
}